import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, ArrowUpRight, Banknote, Bell, Bot, Check, CheckCheck, ChevronDown, ChevronLeft,
  CircleCheck, EllipsisVertical, LayoutGrid, LockOpen, Megaphone, Menu, MessageCircle, Mic,
  Paperclip, Play, QrCode, Radar, ShieldHalf, Smile, Tags, TrendingUp, Users, WandSparkles,
  Workflow, X, Zap,
} from 'lucide-react';
import Facet from '../../components/Brand/Facet';
import './index.css';

import logo from '../../static/images/logo.png';
import banner from '../../static/images/landing_page/banner4.png';
import pushinpayLogo from '../../static/images/gateways/pushinpay.webp';
import syncpayLogo from '../../static/images/gateways/syncpay.webp';
import wiinpayLogo from '../../static/images/gateways/wiinpay2.jpeg';
import omegapayLogo from '../../static/images/gateways/omegapay.webp';
import oasyfyLogo from '../../static/images/gateways/oasyfy.webp';
import nexusLogo from '../../static/images/gateways/nexuspag.webp';
import jumpfyLogo from '../../static/images/gateways/jumpfy.webp';
import atenaspayLogo from '../../static/images/gateways/atenaspay.webp';
import stripeLogo from '../../static/images/gateways/stripe.webp';
import cooudLogo from '../../static/images/gateways/cooud.webp';
import eagleLogo from '../../static/images/gateways/eagle.webp';
import xpagLogo from '../../static/images/gateways/xpag.webp';

/* ---------------------------------------------------------------- */
/* Simulação do Telegram (tema escuro do app) — uma venda em loop    */
/* ---------------------------------------------------------------- */
const CHAT_DELAYS = [700, 900, 1100, 1100, 900, 1100, 1500, 3200];

function Time({ read = false }) {
  return (
    <span className="tg-time">
      12:04{read && <CheckCheck size={14} aria-hidden="true" />}
    </span>
  );
}

function TelegramMock() {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setStep((s) => (s >= CHAT_DELAYS.length - 1 ? 0 : s + 1));
    }, CHAT_DELAYS[step]);
    return () => clearTimeout(timer);
  }, [step]);

  return (
    <div className="tg-wrap">
      <div className="tg" aria-label="Exemplo de venda acontecendo no Telegram">
        <div className="tg-head">
          <ChevronLeft size={22} className="tg-head-muted" aria-hidden="true" />
          <div className="tg-avatar">S</div>
          <div className="tg-head-text">
            <div className="tg-name">Spectrum VIP Bot</div>
            <div className="tg-status">bot</div>
          </div>
          <EllipsisVertical size={20} className="tg-head-muted" aria-hidden="true" />
        </div>

        <div className="tg-chat">
          <div className="tg-day">Hoje</div>

          {step >= 1 && (
            <div className="tg-bubble tg-out">/start <Time read /></div>
          )}

          {step === 2 && (
            <div className="tg-bubble tg-in tg-typing" aria-hidden="true"><span /><span /><span /></div>
          )}

          {step >= 3 && (
            <div className="tg-group">
              <div className="tg-bubble tg-in">
                🔥 <b>Bem-vindo ao Grupo VIP!</b><br />Escolha seu plano de acesso:
                <Time />
              </div>
              <div className="tg-keyboard">
                <div className="tg-key">Mensal — R$ 29,90</div>
                <div className={`tg-key ${step >= 4 ? 'is-pressed' : ''}`}>Vitalício — R$ 97,00</div>
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="tg-bubble tg-in tg-typing" aria-hidden="true"><span /><span /><span /></div>
          )}

          {step >= 6 && (
            <div className="tg-group">
              <div className="tg-bubble tg-in">
                💠 <b>PIX gerado!</b> Copie o código:
                <div className="tg-code">00020126580014br.gov.bcb.pix013…</div>
                <Time />
              </div>
              <div className="tg-keyboard">
                <div className="tg-key">📋 Copiar código PIX</div>
              </div>
            </div>
          )}

          {step >= 7 && (
            <div className="tg-service">
              <CircleCheck size={14} aria-hidden="true" /> Pagamento aprovado — acesso liberado!
            </div>
          )}
        </div>

        <div className="tg-input" aria-hidden="true">
          <Smile size={22} />
          <span>Mensagem</span>
          <Paperclip size={20} />
          <Mic size={20} />
        </div>
      </div>

      <div className={`sale-toast ${step >= 7 ? 'is-shown' : ''}`} aria-hidden={step < 7}>
        <div className="sale-toast-icon"><Zap size={16} aria-hidden="true" /></div>
        <div>
          <div className="sale-toast-title">Venda aprovada</div>
          <div className="sale-toast-value">+ R$ 97,00</div>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* Contador que anima ao entrar na tela                              */
/* ---------------------------------------------------------------- */
function Counter({ end, suffix = '', duration = 1800 }) {
  const ref = useRef(null);
  const [value, setValue] = useState(0);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || started.current) return;
        started.current = true;
        const t0 = performance.now();
        const tick = (now) => {
          const p = Math.min((now - t0) / duration, 1);
          setValue(Math.round(end * (1 - Math.pow(1 - p, 3))));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      },
      { threshold: 0.4 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [end, duration]);

  return <span ref={ref}>{value.toLocaleString('pt-BR')}{suffix}</span>;
}

/* ---------------------------------------------------------------- */
/* Conteúdo                                                          */
/* ---------------------------------------------------------------- */
const NAV = [
  { href: '#recursos', label: 'Recursos' },
  { href: '#como-funciona', label: 'Como funciona' },
  { href: '#precos', label: 'Preços' },
  { href: '#faq', label: 'Dúvidas' },
];

const FEATURES = [
  { Icon: Workflow, title: 'Flow Builder Visual', desc: 'Monte funis de venda arrastando blocos: mensagens, mídias, botões e cobranças conectados visualmente, sem código.' },
  { Icon: Megaphone, title: 'Disparo em Massa', desc: 'Envie campanhas para toda sua base com agendamento, múltiplos bots e estatísticas de entrega em tempo real.' },
  { Icon: Tags, title: 'Downsell Inteligente', desc: 'Recupere quem não comprou com ofertas automáticas de valor reduzido, no tempo certo.' },
  { Icon: TrendingUp, title: 'Upsell & Order Bump', desc: 'Aumente o ticket médio com ofertas complementares no momento exato da compra.' },
  { Icon: Radar, title: 'Tracking & Cloaker', desc: 'Rastreie campanhas com UTMs, integre com UTMify e proteja seu funil com cloaker nativo.' },
  { Icon: ShieldHalf, title: 'Proteção Anti-Clonagem', desc: 'Mecanismos que dificultam cópias do seu bot e acessos não autorizados.' },
  { Icon: LayoutGrid, title: 'Gestão Centralizada', desc: 'Todos os seus bots, planos, membros e automações em um único painel — sem limite de bots.' },
  { Icon: Bell, title: 'Notificações em Tempo Real', desc: 'Push de cada venda aprovada no seu celular. Acompanhe sua operação de onde estiver.' },
  { Icon: Users, title: 'Gestão de Assinaturas', desc: 'Cobranças recorrentes, controle de acessos e remoções automáticas ao fim do plano.' },
];

const STEPS = [
  { Icon: Bot, title: 'Conecte seu bot', desc: 'Cole o token do seu bot do Telegram e ele fica online em segundos. Sem servidor, sem configuração técnica.' },
  { Icon: WandSparkles, title: 'Monte seu funil', desc: 'Crie a jornada de venda no editor visual: boas-vindas, mídias, planos, order bump, upsell e remarketing.' },
  { Icon: Banknote, title: 'Venda no automático', desc: 'Cobrança gerada na conversa, pagamento confirmado em segundos e acesso liberado sozinho. Você só acompanha os números.' },
];

const FAQS = [
  { q: 'Como recebo meus pagamentos?', a: 'Os pagamentos caem direto na sua conta do gateway que você conectar (PushinPay, SyncPay, Stripe e os outros da lista acima). O dinheiro nunca passa pela Spectrum — você tem controle total dos seus recebimentos.' },
  { q: 'Qual é a taxa da plataforma?', a: 'Apenas R$ 0,50 por venda aprovada. Sem mensalidade, sem taxa de adesão, sem custo oculto. Se você não vende, não paga nada.' },
  { q: 'Preciso saber programar?', a: 'Não. Todo o funil é montado em um editor visual de arrastar e soltar. Você conecta o token do bot, configura mensagens e planos, e está no ar.' },
  { q: 'Quantos bots posso criar?', a: 'Quantos quiser. A plataforma foi feita para escalar: gerencie dezenas de bots e grupos VIP em um único painel, com estatísticas separadas por bot.' },
  { q: 'O acesso ao grupo é liberado automaticamente?', a: 'Sim. Assim que o pagamento é confirmado, o cliente recebe o link de acesso na hora. Quando a assinatura vence, a remoção também é automática.' },
  { q: 'É seguro confiar na Spectrum?', a: 'Sim. Usamos criptografia, os pagamentos vão direto para o seu gateway e seus dados ficam privados. Além disso, você conta com suporte humano para acompanhar sua operação.' },
];

// O mesmo catálogo do backend (src/database/models.py): PIX e cartão.
const GATEWAYS = [
  { img: pushinpayLogo, name: 'PushinPay' },
  { img: syncpayLogo, name: 'SyncPay' },
  { img: wiinpayLogo, name: 'WiinPay' },
  { img: omegapayLogo, name: 'OmegaPay' },
  { img: oasyfyLogo, name: 'Oasyfy' },
  { img: nexusLogo, name: 'NexusPag' },
  { img: jumpfyLogo, name: 'Jumpfy' },
  { img: atenaspayLogo, name: 'AtenasPay' },
  { img: stripeLogo, name: 'Stripe' },
  { img: cooudLogo, name: 'Cooud' },
  { img: eagleLogo, name: 'Eagle' },
  { img: xpagLogo, name: 'XPag' },
];

const PRICING = [
  'Bots, fluxos e disparos ilimitados',
  'Todos os recursos liberados desde o primeiro dia',
  'Pagamentos direto na sua conta do gateway',
  'Suporte humano acompanhando sua operação',
  'Escala automática em picos de venda',
];

/* ---------------------------------------------------------------- */
/* Página                                                            */
/* ---------------------------------------------------------------- */
function LandingPage() {
  const [openFaq, setOpenFaq] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const handleNavClick = (e, target) => {
    e.preventDefault();
    setMenuOpen(false);
    document.querySelector(target)?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    // Quem rola é o <body> (global.css/index.css: html, body { height: 100% }),
    // não a janela: escuta a rolagem de qualquer elemento (captura).
    const scrollTop = () => Math.max(window.scrollY, document.body.scrollTop, document.documentElement.scrollTop);
    const onScroll = () => setScrolled(scrollTop() > 8);
    onScroll();
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });

    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      }),
      { threshold: 0.12 }
    );
    document.querySelectorAll('.ld-reveal').forEach((el) => observer.observe(el));

    return () => {
      document.removeEventListener('scroll', onScroll, { capture: true });
      observer.disconnect();
    };
  }, []);

  return (
    <div className="ld-root">
      {/* HEADER */}
      <header className={`ld-header ${scrolled || menuOpen ? 'is-solid' : ''}`}>
        <div className="ld-header-inner">
          <Link to="/" className="ld-logo" aria-label="Spectrum — início">
            <img src={logo} alt="Spectrum" />
          </Link>

          <nav className="ld-nav" aria-label="Seções">
            {NAV.map((n) => (
              <a key={n.href} href={n.href} onClick={(e) => handleNavClick(e, n.href)}>{n.label}</a>
            ))}
          </nav>

          <div className="ld-header-actions">
            <Link to="/login" className="ld-btn ld-btn-ghost ld-btn-sm">Login</Link>
            <Link to="/register" className="ld-btn ld-btn-sm">Criar conta</Link>
            <button
              type="button"
              className="ld-menu-toggle"
              aria-label={menuOpen ? 'Fechar menu' : 'Abrir menu'}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((o) => !o)}
            >
              {menuOpen ? <X size={22} aria-hidden="true" /> : <Menu size={22} aria-hidden="true" />}
            </button>
          </div>
        </div>

        <div className={`ld-mobile-menu ${menuOpen ? 'is-open' : ''}`}>
          {NAV.map((n) => (
            <a key={n.href} href={n.href} onClick={(e) => handleNavClick(e, n.href)}>{n.label}</a>
          ))}
          <div className="ld-mobile-actions">
            <Link to="/login" className="ld-btn ld-btn-ghost">Login</Link>
            <Link to="/register" className="ld-btn">Criar conta</Link>
          </div>
        </div>
      </header>

      <main>
        {/* HERO — o único lugar com o título vazado */}
        <section className="ld-hero">
          <div className="ld-wrap ld-hero-grid">
            <div className="ld-hero-copy">
              <p className="ld-eyebrow ld-in ld-in-1"><span className="ld-eyebrow-line" /> Automação de vendas no Telegram</p>
              <h1 className="ld-h1 ld-in ld-in-2">
                <span>Seu VIP vendendo 24h</span>
                <span className="ld-accent">no automático.</span>
              </h1>
              <p className="ld-lede ld-in ld-in-3">
                A Spectrum cria o funil de vendas, gera a cobrança na conversa,
                confirma o pagamento e libera o acesso — enquanto você só
                acompanha as notificações de venda no celular.
              </p>
              <div className="ld-cta-row ld-in ld-in-3">
                <Link to="/register" className="ld-btn">
                  Começar grátis <ArrowRight size={16} aria-hidden="true" />
                </Link>
                <a href="#como-funciona" className="ld-btn ld-btn-ghost"
                  onClick={(e) => handleNavClick(e, '#como-funciona')}>
                  <Play size={15} aria-hidden="true" /> Ver como funciona
                </a>
              </div>
              <ul className="ld-hero-facts ld-in ld-in-4">
                <li><Check size={15} aria-hidden="true" /> Sem mensalidade</li>
                <li><Check size={15} aria-hidden="true" /> Sem limite de bots</li>
                <li><Check size={15} aria-hidden="true" /> Online em minutos</li>
              </ul>
            </div>

            <div className="ld-hero-visual ld-in ld-in-2">
              <TelegramMock />
            </div>
          </div>
        </section>

        {/* GATEWAYS — faixa que desliza (duas cópias para emendar o loop) */}
        <section className="ld-gateways" aria-label="Gateways compatíveis">
          <p className="ld-gateways-label">Receba direto na sua conta com {GATEWAYS.length} gateways</p>
          <div className="ld-marquee">
            <div className="ld-marquee-track">
              {[0, 1].map((copy) => (
                <ul key={copy} className="ld-marquee-list" aria-hidden={copy === 1}>
                  {GATEWAYS.map((g) => (
                    <li key={g.name}>
                      <img src={g.img} alt="" />
                      <span>{g.name}</span>
                    </li>
                  ))}
                </ul>
              ))}
            </div>
          </div>
        </section>

        {/* NÚMEROS */}
        <section className="ld-wrap ld-stats" aria-label="Números">
          {[
            { v: <Counter end={2} suffix=" seg" />, l: 'para confirmar um PIX' },
            { v: <Counter end={100} suffix="%" />, l: 'do pagamento é seu' },
            { v: 'R$ 0,50', l: 'taxa fixa por venda' },
            { v: <Counter end={24} suffix="/7" />, l: 'vendendo sem parar' },
          ].map((s, i) => (
            <div key={s.l} className={`ld-stat ld-reveal ld-d${i}`}>
              <div className="ld-stat-value">{s.v}</div>
              <div className="ld-stat-label">{s.l}</div>
            </div>
          ))}
        </section>

        {/* RECURSOS */}
        <section className="ld-section" id="recursos">
          <div className="ld-wrap">
            <div className="ld-head is-center ld-reveal">
              <p className="ld-eyebrow">Recursos</p>
              <h2 className="ld-h2">Tudo num só painel</h2>
              <p className="ld-head-sub">
                Do primeiro clique no anúncio até a renovação da assinatura — cada
                etapa do funil é automatizada e mensurável.
              </p>
            </div>

            <ul className="ld-features">
              {FEATURES.map(({ Icon, title, desc }, i) => (
                <li key={title} className={`ld-feature ld-reveal ld-d${i % 3}`}>
                  <span className="ld-feature-icon"><Icon size={22} strokeWidth={1.75} aria-hidden="true" /></span>
                  <h3>{title}</h3>
                  <p>{desc}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* FLOW BUILDER */}
        <section className="ld-section">
          <div className="ld-wrap ld-split">
            <div className="ld-reveal">
              <p className="ld-eyebrow">Flow Builder</p>
              <h2 className="ld-h2">Desenhe o funil.<br />A Spectrum executa.</h2>
              <p className="ld-body">
                Esqueça configurações em texto. No editor visual você conecta
                blocos de mensagem, mídia, botões e cobrança — e vê exatamente
                o caminho que seu cliente vai percorrer até o pagamento.
              </p>
              <ul className="ld-checks">
                <li><Check size={16} aria-hidden="true" /> Arraste e solte blocos, sem código</li>
                <li><Check size={16} aria-hidden="true" /> Textos, fotos, vídeos, áudios e botões</li>
                <li><Check size={16} aria-hidden="true" /> Cobrança em qualquer ponto do fluxo</li>
              </ul>
            </div>

            <div className="ld-reveal ld-d1">
              <div className="ld-flow" aria-hidden="true">
                {[
                  { Icon: MessageCircle, t: 'Boas-vindas', s: 'Mensagem + vídeo' },
                  { Icon: QrCode, t: 'Cobrança PIX', s: 'Plano Vitalício — R$ 97' },
                  { Icon: LockOpen, t: 'Acesso liberado', s: 'Link do grupo VIP' },
                ].map(({ Icon, t, s }, i) => (
                  <React.Fragment key={t}>
                    {i > 0 && <div className="ld-flow-line" />}
                    <div className="ld-flow-node">
                      <span className="ld-flow-icon"><Icon size={18} strokeWidth={1.9} /></span>
                      <div>
                        <div className="ld-flow-title">{t}</div>
                        <div className="ld-flow-sub">{s}</div>
                      </div>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* DISPARO */}
        <section className="ld-section">
          <div className="ld-wrap ld-split ld-split-reverse">
            <div className="ld-reveal ld-d1">
              <div className="ld-broadcast" aria-hidden="true">
                <div className="ld-broadcast-head">
                  <span className="ld-flow-icon"><Megaphone size={18} strokeWidth={1.9} /></span>
                  <span>Disparo — Promoção Relâmpago</span>
                  <span className="ld-broadcast-badge"><span className="ld-live-dot" /> Enviando</span>
                </div>
                <div className="ld-broadcast-row"><span>Leads alcançados</span><b>8.4k</b></div>
                <div className="ld-broadcast-bar"><div /></div>
                <dl className="ld-broadcast-stats">
                  <div><dd className="is-ok">97%</dd><dt>entregues</dt></div>
                  <div><dd className="is-purple">312</dd><dt>cliques</dt></div>
                  <div><dd className="is-amber">48</dd><dt>vendas</dt></div>
                </dl>
              </div>
            </div>

            <div className="ld-reveal">
              <p className="ld-eyebrow">Remarketing & Disparo</p>
              <h2 className="ld-h2">Sua base de leads virando receita de novo</h2>
              <p className="ld-body">
                Quem entrou no bot e não comprou não está perdido. Dispare
                campanhas segmentadas, agende promoções e deixe o remarketing
                automático recuperar vendas enquanto você dorme.
              </p>
              <ul className="ld-checks">
                <li><Check size={16} aria-hidden="true" /> Disparo em massa com agendamento</li>
                <li><Check size={16} aria-hidden="true" /> Remarketing automático pós-abandono</li>
                <li><Check size={16} aria-hidden="true" /> Downsell com desconto progressivo</li>
              </ul>
            </div>
          </div>
        </section>

        {/* PAINEL */}
        <section className="ld-section">
          <div className="ld-wrap">
            <div className="ld-head is-center ld-reveal">
              <p className="ld-eyebrow">Painel</p>
              <h2 className="ld-h2">Cada real rastreado, em tempo real</h2>
              <p className="ld-head-sub">
                Faturamento, conversão, ranking de bots e origem de cada venda.
                Decisões com dados, não com achismo.
              </p>
            </div>
            <div className="ld-browser ld-reveal ld-d1">
              <div className="ld-browser-bar">
                <span /><span /><span />
                <div className="ld-browser-url">spectrumbots.online</div>
              </div>
              <div className="ld-browser-shot">
                {/* Tamanho reservado: carregar sob demanda não empurra as âncoras de baixo */}
                <img src={banner} alt="Painel da Spectrum com faturamento e vendas" loading="lazy"
                  width="1890" height="902" />
              </div>
            </div>
          </div>
        </section>

        {/* COMO FUNCIONA */}
        <section className="ld-section" id="como-funciona">
          <div className="ld-wrap">
            <div className="ld-head is-center ld-reveal">
              <p className="ld-eyebrow">Como funciona</p>
              <h2 className="ld-h2">Do zero ao ar em 3 passos</h2>
            </div>
            <ol className="ld-steps">
              {STEPS.map(({ Icon, title, desc }, i) => (
                <li key={title} className={`ld-step ld-reveal ld-d${i}`}>
                  <div className="ld-step-top">
                    <span className="ld-step-num">{i + 1}</span>
                    <Icon size={22} strokeWidth={1.75} aria-hidden="true" />
                  </div>
                  <h3>{title}</h3>
                  <p>{desc}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* PREÇOS */}
        <section className="ld-section" id="precos">
          <div className="ld-wrap">
            <div className="ld-price ld-reveal">
              <div className="ld-price-main">
                <p className="ld-eyebrow">Preços</p>
                <h2 className="ld-h2">Você só paga quando vende</h2>
                <p className="ld-price-value"><span>R$</span>0,50</p>
                <p className="ld-price-per">por venda aprovada · sem mensalidade, sem plano, sem surpresa</p>
                <Link to="/register" className="ld-btn">
                  Criar conta grátis <ArrowRight size={16} aria-hidden="true" />
                </Link>
              </div>
              <ul className="ld-price-list">
                {PRICING.map((item) => (
                  <li key={item}><span className="ld-check-dot"><Check size={14} aria-hidden="true" /></span> {item}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="ld-section" id="faq">
          <div className="ld-wrap ld-faq-wrap">
            <div className="ld-head is-center ld-reveal">
              <p className="ld-eyebrow">Dúvidas</p>
              <h2 className="ld-h2">Perguntas frequentes</h2>
            </div>

            <div className="ld-faq">
              {FAQS.map((f, i) => {
                const open = openFaq === i;
                return (
                  <div key={f.q} className={`ld-faq-item ${open ? 'is-open' : ''}`}>
                    <button
                      type="button"
                      className="ld-faq-q"
                      aria-expanded={open}
                      aria-controls={`ld-faq-${i}`}
                      id={`ld-faq-q-${i}`}
                      onClick={() => setOpenFaq(open ? -1 : i)}
                    >
                      <span>{f.q}</span>
                      <ChevronDown className="ld-faq-chevron" size={20} aria-hidden="true" />
                    </button>
                    <div id={`ld-faq-${i}`} role="region" aria-labelledby={`ld-faq-q-${i}`}
                      className="ld-faq-a" hidden={!open}>
                      <p>{f.a}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA FINAL — faixa roxa da marca */}
        <section className="ld-section">
          <div className="ld-wrap">
            <div className="ld-cta ld-reveal">
              <div className="ld-cta-copy">
                <h2 className="ld-cta-title">Pronto para vender enquanto dorme?</h2>
                <p>
                  Crie sua conta, conecte seu bot e coloque seu grupo VIP no ar
                  ainda hoje. Sem mensalidade, sem risco.
                </p>
                <Link to="/register" className="ld-btn ld-btn-light">
                  Começar agora — é grátis <ArrowRight size={16} aria-hidden="true" />
                </Link>
              </div>
              <Facet className="ld-cta-facet" />
            </div>
          </div>
        </section>
      </main>

      {/* FOOTER */}
      <footer className="ld-footer">
        <div className="ld-wrap">
          <div className="ld-footer-grid">
            <div className="ld-footer-brand">
              <img src={logo} alt="Spectrum" />
              <p>A plataforma para gerenciar grupos pagos no Telegram com automação total.</p>
              <a href="https://www.instagram.com/spectrumvips/" className="ld-footer-social" target="_blank" rel="noreferrer">
                Instagram @spectrumvips <ArrowUpRight size={14} aria-hidden="true" />
              </a>
            </div>

            <div>
              <h4>Produto</h4>
              <ul>
                {NAV.map((n) => (
                  <li key={n.href}><a href={n.href} onClick={(e) => handleNavClick(e, n.href)}>{n.label}</a></li>
                ))}
              </ul>
            </div>

            <div>
              <h4>Legal</h4>
              <ul>
                <li><Link to="/legal/tos">Termos de Uso</Link></li>
              </ul>
            </div>

            <div>
              <h4>Suporte</h4>
              <ul>
                <li>
                  <a href="https://api.whatsapp.com/send/?phone=558399824345&text=Ol%C3%A1%21+Preciso+de+suporte+com+o+SPECTRUM&type=phone_number&app_absent=0"
                    target="_blank" rel="noreferrer">
                    Contato
                  </a>
                </li>
                <li><Link to="/register">Criar conta</Link></li>
                <li><Link to="/login">Login</Link></li>
              </ul>
            </div>
          </div>

          <div className="ld-footer-bottom">
            <p>© {new Date().getFullYear()} Spectrum. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
